import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace, useExpenses } from '@/hooks/useData';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { toIsoDate } from '@/lib/format';
import { uploadFileToDrive, isDriveConnected, DriveNotConnectedError, DriveAuthRevokedError } from '@/lib/driveUpload';
import { compressImage } from '@/lib/imageCompress';
import { TodueWordmark } from '@/components/Logo';
import GlobalSearchResults from '@/components/GlobalSearchResults';
import { SECTIONS as CENTRAL_SECTIONS } from '@/lib/sections';
import { FEATURES } from '@/lib/featureFlags';
import MobileFan from '@/components/MobileFan';
import './MobileTopbar.css';

// Single source of truth: derive mobile menu from @/lib/sections.
// Excluded: 'prehlad' (homed via todue logo button), 'nastavenia' (via ⚙ on card).
// Prepended: 'search' (mobile-only reveal pattern).
const SECTIONS = [
  { id: 'search', label: 'Hľadať', color: 'blue', icon: 'search', action: 'search' },
  ...CENTRAL_SECTIONS
    .filter(s => !['prehlad', 'nastavenia'].includes(s.id))
    .filter(s => !s.flag || FEATURES[s.flag]),
];

// ── Capture (+) items — quick actions
const CAPTURE = [
  { id: 'qr',    label: 'Sken QR bločku', color: 'yellow', icon: 'qr',     action: 'qr'     },
  { id: 'photo', label: 'Odfoť doklad',   color: 'blue',   icon: 'camera', action: 'photo'  },
  { id: 'fa',    label: 'Nová faktúra',   color: 'green',  icon: 'doc',    action: 'newfa'  },
  // Nová zákazka — denná akcia, preto pod „+" vedľa faktúry (DESIGN-SYSTEM.md §2b).
  // Skrytá spolu so sekciou, kým je Dielňa za V2 beta flagom.
  ...(FEATURES.zakazky
    ? [{ id: 'zak', label: 'Nová zákazka', color: 'yellow', icon: 'services', action: 'newzak' }]
    : []),
];

export default function MobileTopbar() {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const { create: createExpense } = useExpenses(workspace?.id);
  const navigate = useNavigate();
  const toast = useToast();

  const [menuOpen, setMenuOpen] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuBtnRef = useRef(null);
  const capBtnRef = useRef(null);
  const fileRef = useRef(null);
  const searchInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const closeAll = useCallback(() => {
    setMenuOpen(false);
    setCapOpen(false);
    setSearchOpen(false);
  }, []);

  // ESC closes
  useEffect(() => {
    if (!menuOpen && !capOpen && !searchOpen) return;
    const onKey = (e) => e.key === 'Escape' && closeAll();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, capOpen, searchOpen, closeAll]);

  // Auto-focus search input when opened
  useEffect(() => {
    if (searchOpen) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 280);
      return () => clearTimeout(t);
    }
    setSearchQuery('');
  }, [searchOpen]);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setCapOpen(false);
    setSearchOpen(false);
    setMenuOpen((v) => !v);
  };
  const handleCapClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setSearchOpen(false);
    setCapOpen((v) => !v);
  };
  const handleHome = () => {
    closeAll();
    navigate('/');
  };

  const handleSectionPick = (item) => {
    if (item.action === 'search') {
      setMenuOpen(false);
      setCapOpen(false);
      setSearchOpen(true);
      return;
    }
    closeAll();
    if (item.path) navigate(item.path);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchOpen(false);
    toast.show?.('Hľadanie zatiaľ neimplementované — pripravujeme.');
  };

  const handleCapturePick = (item) => {
    closeAll();
    if (item.action === 'newfa') {
      navigate('/faktury/nova');
      return;
    }
    if (item.action === 'newzak') {
      navigate('/zakazky/nova');
      return;
    }
    if (item.action === 'photo' || item.action === 'qr') {
      // both trigger camera file picker; in real impl QR could route to scanner modal
      fileRef.current?.click();
    }
  };

  // Capture file → Drive upload + create expense (mirror QuickCaptureFab)
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !workspace || !user) return;
    setBusy(true);
    try {
      const connected = await isDriveConnected();
      if (!connected) {
        toast.error('Najprv pripoj Google Drive v Nastaveniach.');
        return;
      }
      const compressed = await compressImage(file);
      const uploadFile = compressed.file;
      const result = await uploadFileToDrive({
        file: uploadFile,
        workspace_id: workspace.id,
        year: new Date().getFullYear(),
        type: 'vydavky',
      });
      await createExpense({
        vendor_name: null, vendor_ico: null, amount: 0,
        dph_rate: workspace.default_dph_rate || 0, dph_amount: 0,
        date: toIsoDate(), category: 'other',
        description: 'Rýchly záber — doplň polia.',
        paid_via: 'card',
        gdrive_file_id: result.gdrive_file_id,
      });
      toast.success('Doklad uložený do Drive. Polia doplníš v sekcii Výdavky.');
    } catch (err) {
      if (err instanceof DriveNotConnectedError || err instanceof DriveAuthRevokedError) {
        toast.error(err.message);
      } else {
        toast.error('Upload zlyhal: ' + (err.message || err));
      }
    } finally {
      setBusy(false);
    }
  };

  const open = menuOpen || capOpen || searchOpen;

  return (
    <div className={`mt-root ${open ? 'is-open' : ''} ${searchOpen ? 'is-search' : ''}`}>
      <div className="mt-backdrop" onClick={closeAll} aria-hidden="true" />

      <div className="mt-bar">
        <button
          ref={menuBtnRef}
          type="button"
          className={`mt-cbtn mt-menu-btn ${menuOpen ? 'is-open' : ''}`}
          onClick={handleMenuClick}
          aria-label="Menu"
        >
          <svg viewBox="0 0 24 24">
            <line className="ln-a" x1="4" y1="6.5" x2="20" y2="6.5" />
            <line className="ln-b" x1="4" y1="12"  x2="20" y2="12"  />
            <line className="ln-c" x1="4" y1="17.5" x2="20" y2="17.5" />
          </svg>
        </button>

        <button type="button" className="mt-home" onClick={handleHome} aria-label="Domov">
          <TodueWordmark height={28} />
        </button>

        <button
          ref={capBtnRef}
          type="button"
          className={`mt-cbtn mt-cap-btn ${capOpen ? 'is-open' : ''}`}
          onClick={handleCapClick}
          aria-label="Pridať / skenovať"
          disabled={busy}
        >
          <svg viewBox="0 0 24 24">
            <line x1="12" y1="4" x2="12" y2="20" />
            <line x1="4" y1="12" x2="20" y2="12" />
          </svg>
        </button>
      </div>

      <MobileFan
        items={SECTIONS}
        anchorRef={menuBtnRef}
        active={menuOpen}
        dir="r"
        onPick={handleSectionPick}
      />
      <MobileFan
        items={CAPTURE}
        anchorRef={capBtnRef}
        active={capOpen}
        dir="l"
        onPick={handleCapturePick}
      />

      <form
        className={`mt-search ${searchOpen ? 'is-open' : ''}`}
        onSubmit={handleSearchSubmit}
        onClick={(e) => e.stopPropagation()}
        role="search"
      >
        <svg className="mt-search-ico" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          ref={searchInputRef}
          type="search"
          className="mt-search-input"
          placeholder="Hľadať klientov, faktúry, doklady…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
        />
        <button
          type="button"
          className="mt-search-close"
          onClick={() => setSearchOpen(false)}
          aria-label="Zavrieť vyhľadávanie"
        >
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </form>

      {searchOpen && (
        <div className="mt-search-results-wrap" onClick={(e) => e.stopPropagation()}>
          <GlobalSearchResults
            query={searchQuery}
            onPick={() => setSearchOpen(false)}
            variant="panel"
          />
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
    </div>
  );
}

